import React from "react";
import { Button } from "../ui/button";
import { BRAND } from "../../brand.js";
import { Separator } from "../ui/separator";
import { cn } from "../../lib/utils";
import { resolveCopyrightYear } from "../../lib/copyright-year";
import { type BaseComponentProps } from "../../types/puck";
import { Facebook, Twitter, Linkedin, Instagram, MapPin, Phone, Mail } from "lucide-react";

export interface FooterLink {
  label: string;
  linkType: "page" | "url" | "anchor";
  pageId?: string;
  url?: string;
}

export interface BusinessHours {
  day: string;
  hours: string;
  closed?: boolean;
}

export interface FooterProps extends BaseComponentProps {
  logo?: string;
  companyName: string;
  /** Controls logo/text display: "logo" = logo only (text fallback), "text" = text only, "both" = show both */
  logoDisplay?: "logo" | "text" | "both";
  description: string;
  linkColumns: Array<{
    title: string;
    links: Array<FooterLink>;
  }>;
  showSocial: "yes" | "no";
  socialLinks?: {
    facebook?: string;
    twitter?: string;
    linkedin?: string;
    instagram?: string;
  };
  showContact: "yes" | "no";
  email?: string;
  phone?: string;
  address?: string;
  showBusinessHours?: "yes" | "no";
  businessHours?: BusinessHours[];
  copyrightText: string;
  style: "simple" | "multi-column" | "centered";
  /** Render the "Powered by ServicePro.co" badge. Forced on for default-plan
   *  sites by the metadata resolver — see resolveSiteBusinessMetadata. */
  showServiceProBadge?: boolean;
}

export const Footer: React.FC<FooterProps> = ({
  logo,
  companyName,
  logoDisplay = "logo",
  description,
  linkColumns,
  showSocial,
  socialLinks,
  showContact,
  email,
  phone,
  address,
  showBusinessHours,
  businessHours,
  copyrightText,
  style,
  showServiceProBadge = true,
  id,
  className,
}) => {
  // Determine what to show based on logoDisplay setting
  const showLogo = logo && logoDisplay !== "text";
  const showCompanyName = logoDisplay === "text" || logoDisplay === "both" || !logo;
  // Helper function to resolve link URLs - simplified without usePages
  const getUrl = (link: FooterLink) => {
    return link.url || "#";
  };

  const SocialIcons = () => {
    if (showSocial !== "yes" || !socialLinks) return null;

    return (
      <div className="jobsite:flex jobsite:gap-3">
        {socialLinks.facebook && (
          <Button
            size="icon"
            variant="default"
            className="jobsite:w-10 jobsite:h-10 jobsite:rounded-full"
            asChild
          >
            <a
              href={socialLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <Facebook className="jobsite:w-5 jobsite:h-5" />
            </a>
          </Button>
        )}
        {socialLinks.twitter && (
          <Button
            size="icon"
            variant="default"
            className="jobsite:w-10 jobsite:h-10 jobsite:rounded-full"
            asChild
          >
            <a
              href={socialLinks.twitter}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Twitter"
            >
              <Twitter className="jobsite:w-5 jobsite:h-5" />
            </a>
          </Button>
        )}
        {socialLinks.linkedin && (
          <Button
            size="icon"
            variant="default"
            className="jobsite:w-10 jobsite:h-10 jobsite:rounded-full"
            asChild
          >
            <a
              href={socialLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <Linkedin className="jobsite:w-5 jobsite:h-5" />
            </a>
          </Button>
        )}
        {socialLinks.instagram && (
          <Button
            size="icon"
            variant="default"
            className="jobsite:w-10 jobsite:h-10 jobsite:rounded-full"
            asChild
          >
            <a
              href={socialLinks.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <Instagram className="jobsite:w-5 jobsite:h-5" />
            </a>
          </Button>
        )}
      </div>
    );
  };

  const getFooterLayout = () => {
    if (style === "centered") {
      return (
        <div className="jobsite:text-center jobsite:max-w-3xl jobsite:mx-auto">
          {showLogo && (
            <img
              src={logo}
              alt={companyName}
              crossOrigin="anonymous"
              className="jobsite:h-12 jobsite:w-auto jobsite:object-contain jobsite:mx-auto jobsite:mb-4"
            />
          )}
          {showCompanyName && (
            <h3 className="jobsite:text-2xl jobsite:mb-4 jobsite:text-primary jobsite:font-heading">
              {companyName}
            </h3>
          )}
          <p className="jobsite:mb-6 jobsite:text-lg jobsite:text-foreground/80">{description}</p>

          {showContact === "yes" && (email || phone || address) && (
            <div className="jobsite:mb-6 jobsite:flex jobsite:flex-col jobsite:items-center jobsite:gap-3">
              {phone && (
                <a
                  href={`tel:${phone.replace(/\D/g, "")}`}
                  className="jobsite:inline-flex jobsite:items-center jobsite:gap-2 jobsite:text-primary jobsite:font-semibold jobsite:hover:underline"
                >
                  <Phone className="jobsite:w-4 jobsite:h-4" />
                  {phone}
                </a>
              )}
              {email && (
                <a
                  href={`mailto:${email}`}
                  className="jobsite:inline-flex jobsite:items-center jobsite:gap-2 jobsite:text-foreground/80 jobsite:transition-colors jobsite:hover:text-primary"
                >
                  <Mail className="jobsite:w-4 jobsite:h-4" />
                  {email}
                </a>
              )}
              {address && (
                <p className="jobsite:inline-flex jobsite:items-start jobsite:gap-2 jobsite:text-foreground/70 jobsite:whitespace-pre-line">
                  <MapPin className="jobsite:w-4 jobsite:h-4 jobsite:mt-0.5 jobsite:shrink-0" />
                  <span>{address}</span>
                </p>
              )}
            </div>
          )}

          {showBusinessHours === "yes" && businessHours && businessHours.length > 0 && (
            <div className="jobsite:mb-6">
              <h4 className=" jobsite:text-sm jobsite:uppercase jobsite:tracking-wider jobsite:mb-3 jobsite:text-muted-foreground jobsite:font-heading">
                Business Hours
              </h4>
              <div className="jobsite:space-y-2 jobsite:text-sm jobsite:text-foreground">
                {businessHours.map((schedule, idx) => (
                  <div key={idx} className="jobsite:flex jobsite:justify-center jobsite:gap-4">
                    <span className="jobsite:font-medium jobsite:w-24 jobsite:text-right">
                      {schedule.day}
                    </span>
                    <span
                      className={cn(
                        "jobsite:w-32 jobsite:text-left",
                        schedule.closed && "jobsite:text-muted-foreground jobsite:italic",
                      )}
                    >
                      {schedule.closed ? "Closed" : schedule.hours}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="jobsite:flex jobsite:justify-center">
            <SocialIcons />
          </div>
        </div>
      );
    }

    if (style === "simple") {
      return (
        <div className="jobsite:space-y-8">
          <div className="jobsite:flex jobsite:flex-col jobsite:md:flex-row jobsite:justify-between jobsite:items-start jobsite:md:items-center jobsite:gap-8">
            <div className="jobsite:flex-1">
              <div className="jobsite:flex jobsite:items-center jobsite:gap-3 jobsite:mb-2">
                {showLogo && (
                  <img
                    src={logo}
                    alt={companyName}
                    crossOrigin="anonymous"
                    className="jobsite:h-10 jobsite:w-auto jobsite:object-contain"
                  />
                )}
                {showCompanyName && (
                  <h3 className="jobsite:text-2xl jobsite:text-primary jobsite:font-heading">
                    {companyName}
                  </h3>
                )}
              </div>
              <p className="jobsite:text-base jobsite:text-foreground/80">{description}</p>
            </div>

            {showContact === "yes" && (email || phone) && (
              <div className="jobsite:flex jobsite:flex-col jobsite:gap-2">
                {phone && (
                  <a
                    href={`tel:${phone.replace(/\D/g, "")}`}
                    className="jobsite:font-semibold jobsite:hover:underline jobsite:text-primary jobsite:flex jobsite:items-center jobsite:gap-2"
                  >
                    <Phone className="jobsite:w-4 jobsite:h-4" />
                    {phone}
                  </a>
                )}
                {email && (
                  <a
                    href={`mailto:${email}`}
                    className="jobsite:text-foreground jobsite:transition-colors jobsite:hover:text-primary jobsite:flex jobsite:items-center jobsite:gap-2"
                  >
                    <Mail className="jobsite:w-4 jobsite:h-4" />
                    {email}
                  </a>
                )}
              </div>
            )}

            <SocialIcons />
          </div>

          {showBusinessHours === "yes" && businessHours && businessHours.length > 0 && (
            <div className="jobsite:border-t jobsite:border-border jobsite:pt-6">
              <h4 className=" jobsite:text-sm jobsite:uppercase jobsite:tracking-wider jobsite:mb-4 jobsite:text-muted-foreground jobsite:font-heading jobsite:text-center">
                Business Hours
              </h4>
              <div className="jobsite:flex jobsite:flex-wrap jobsite:justify-center jobsite:gap-x-8 jobsite:gap-y-2 jobsite:text-sm jobsite:text-foreground">
                {businessHours.map((schedule, idx) => (
                  <div key={idx} className="jobsite:flex jobsite:gap-2">
                    <span className="jobsite:font-medium">{schedule.day}:</span>
                    <span
                      className={cn(
                        schedule.closed && "jobsite:text-muted-foreground jobsite:italic",
                      )}
                    >
                      {schedule.closed ? "Closed" : schedule.hours}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    // multi-column (default)
    return (
      <div className="jobsite:grid jobsite:grid-cols-1 jobsite:md:grid-cols-2 jobsite:lg:grid-cols-4 jobsite:gap-8">
        {/* Company Info Column */}
        <div className="jobsite:lg:col-span-1">
          {showLogo && (
            <img
              src={logo}
              alt={companyName}
              crossOrigin="anonymous"
              className="jobsite:h-10 jobsite:w-auto jobsite:object-contain jobsite:mb-4"
            />
          )}
          {showCompanyName && (
            <h3 className="jobsite:text-2xl jobsite:mb-4 jobsite:text-primary jobsite:font-heading">
              {companyName}
            </h3>
          )}
          <p className="jobsite:text-sm jobsite:mb-4 jobsite:text-foreground/80">{description}</p>
          <SocialIcons />
        </div>

        {/* Link Columns */}
        {linkColumns &&
          linkColumns.map((column, idx) => (
            <div key={idx}>
              <h4 className=" jobsite:text-sm jobsite:uppercase jobsite:tracking-wider jobsite:mb-4 jobsite:text-muted-foreground jobsite:font-heading">
                {column.title}
              </h4>
              <ul className="jobsite:space-y-2">
                {column.links &&
                  column.links.map((link, linkIdx) => (
                    <li key={linkIdx}>
                      <a
                        href={getUrl(link)}
                        className="jobsite:text-sm jobsite:text-foreground/80 jobsite:transition-colors jobsite:hover:text-primary"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
              </ul>
            </div>
          ))}

        {/* Contact Column */}
        {showContact === "yes" && (email || phone || address) && (
          <div>
            <h4 className=" jobsite:text-sm jobsite:uppercase jobsite:tracking-wider jobsite:mb-4 jobsite:text-muted-foreground jobsite:font-heading">
              Contact Us
            </h4>
            <div className="jobsite:space-y-3 jobsite:text-sm jobsite:text-foreground">
              {phone && (
                <p>
                  <a
                    href={`tel:${phone.replace(/\D/g, "")}`}
                    className="jobsite:hover:underline jobsite:text-primary jobsite:flex jobsite:items-center jobsite:gap-2"
                  >
                    <Phone className="jobsite:w-4 jobsite:h-4" />
                    {phone}
                  </a>
                </p>
              )}
              {email && (
                <p>
                  <a
                    href={`mailto:${email}`}
                    className="jobsite:transition-colors jobsite:hover:text-primary jobsite:flex jobsite:items-center jobsite:gap-2"
                  >
                    <Mail className="jobsite:w-4 jobsite:h-4" />
                    {email}
                  </a>
                </p>
              )}
              {address && (
                <p className="jobsite:whitespace-pre-line jobsite:opacity-80 jobsite:flex jobsite:items-start jobsite:gap-2">
                  <MapPin className="jobsite:w-4 jobsite:h-4 jobsite:mt-0.5 jobsite:shrink-0" />
                  <span>{address}</span>
                </p>
              )}
            </div>
          </div>
        )}

        {/* Business Hours Column */}
        {showBusinessHours === "yes" && businessHours && businessHours.length > 0 && (
          <div>
            <h4 className=" jobsite:text-sm jobsite:uppercase jobsite:tracking-wider jobsite:mb-4 jobsite:text-muted-foreground jobsite:font-heading">
              Business Hours
            </h4>
            <div className="jobsite:space-y-2 jobsite:text-sm jobsite:text-foreground">
              {businessHours.map((schedule, idx) => (
                <div key={idx} className="jobsite:flex jobsite:justify-between jobsite:gap-4">
                  <span className="jobsite:font-medium">{schedule.day}</span>
                  <span
                    className={cn(
                      "jobsite:opacity-80",
                      schedule.closed && "jobsite:text-muted-foreground jobsite:italic",
                    )}
                  >
                    {schedule.closed ? "Closed" : schedule.hours}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <footer
      id={id}
      className={cn(
        "jobsite:relative jobsite:z-40 jobsite:bg-background jobsite:border-t jobsite:border-border/70 jobsite:font-body",
        className,
      )}
    >
      <div className="jobsite:container jobsite:mx-auto jobsite:px-4 jobsite:py-12">
        {getFooterLayout()}
      </div>

      {/* Copyright Bar */}
      <Separator />
      <div className="jobsite:bg-muted/50">
        <div className="jobsite:container jobsite:mx-auto jobsite:flex jobsite:flex-col jobsite:items-center jobsite:gap-2 jobsite:px-4 jobsite:py-4 jobsite:sm:flex-row jobsite:sm:justify-between">
          {/*
            The year comes from the clock at render, not from the stored string, so a site
            built in one year still reads correctly in the next. Prerendered HTML carries the
            build-time year until hydration corrects it, which is the one legitimate mismatch.
          */}
          <p
            suppressHydrationWarning
            className="jobsite:text-center jobsite:text-sm jobsite:text-foreground/70 jobsite:sm:text-left"
          >
            {resolveCopyrightYear(copyrightText, new Date().getFullYear())}
          </p>
          {showServiceProBadge && (
            <a
              href="https://servicepro.co?ref=jobsite_footer"
              target="_blank"
              rel="noopener noreferrer"
              className="jobsite:inline-flex jobsite:items-center jobsite:gap-1.5 jobsite:text-xs jobsite:text-foreground/60 jobsite:transition-colors jobsite:hover:text-foreground/80"
            >
              <span>Powered by</span>
              <svg
                viewBox="0 0 512 512"
                xmlns="http://www.w3.org/2000/svg"
                className="jobsite:h-4 jobsite:w-4"
                aria-hidden="true"
              >
                <g transform="matrix(0.0902174,0,0,0.180435,48.5,-366.5)">
                  <rect x="0" y="2300" width="4600" height="2300" fill={BRAND.yellow} />
                </g>
                <g transform="matrix(0.288514,0,0,0.288514,-148.432,-734.842)">
                  <path
                    d="M1662.39,3121.57L1652.63,3212.85L1910.52,3153.24L1931.7,2928.19L967.124,2928.19L908.72,3548.7L1622.42,3548.7L1135.2,3738L1144.96,3646.73L894.035,3704.72L871.852,3940.4L1836.43,3940.4L1895.68,3310.87L1175.17,3310.87L1662.39,3121.57Z"
                    fill="#231F20"
                  />
                </g>
              </svg>
              <span className="jobsite:font-semibold">ServicePro.co</span>
            </a>
          )}
        </div>
      </div>
    </footer>
  );
};
